// Rooster Product Features with TAS Score Impact Analysis
// Based on comprehensive feature analysis and industry data

export interface RoosterFeature {
  id: string;
  name: string;
  category: string;
  description: string;
  whatItDoes: string;
  whyItMatters: string;
  impacts: {
    schedulingVelocity?: number;    // SV - 30% weight
    schedulingEfficiency?: number;  // SE - 25% weight
    candidateExperience?: number;   // CE - 20% weight
    schedulingIntegrity?: number;   // SI - 15% weight
    schedulingUtilization?: number; // SU - 10% weight
  };
  implementationComplexity: 'low' | 'medium' | 'high';
  timeToValue: string;
  icon?: string; // Optional emoji or icon identifier
}

export const ROOSTER_FEATURES: RoosterFeature[] = [
  // Core Interview Scheduling Features
  {
    id: 'autobook',
    name: 'AutoBook (Self-Scheduling)',
    category: 'Core Scheduling',
    description: 'Candidate self-scheduling system allowing candidates to select their own interview times',
    whatItDoes: 'Enables candidates to pick from available time slots without recruiter involvement',
    whyItMatters: 'Reduces scheduler workload by 50-62% and improves candidate experience',
    impacts: {
      schedulingVelocity: 90,
      schedulingEfficiency: 95,
      candidateExperience: 85,
      schedulingIntegrity: 60
    },
    implementationComplexity: 'low',
    timeToValue: '1 week',
    icon: '🚀'
  },
  {
    id: 'manual-scheduling',
    name: 'Manual Interview Scheduling',
    category: 'Core Scheduling',
    description: 'Complete control over interview scheduling with calendar views and interviewer selection',
    whatItDoes: 'Provides schedulers full visibility and control over interview timing',
    whyItMatters: 'Prevents conflicts and ensures optimal scheduling for complex scenarios',
    impacts: {
      schedulingEfficiency: 40,
      schedulingIntegrity: 70,
      schedulingUtilization: 80
    },
    implementationComplexity: 'low',
    timeToValue: 'Immediate',
    icon: '📅'
  },
  {
    id: 'multi-day-scheduling',
    name: 'Multi-Day Interview Scheduling',
    category: 'Core Scheduling',
    description: 'Schedule interviews spanning multiple days in a single workflow',
    whatItDoes: 'Streamlines complex interview processes across multiple sessions',
    whyItMatters: 'Reduces repetitive scheduling tasks and maintains consistency',
    impacts: {
      schedulingEfficiency: 75,
      candidateExperience: 60,
      schedulingIntegrity: 70,
      schedulingUtilization: 50
    },
    implementationComplexity: 'medium',
    timeToValue: '1 week',
    icon: '📆'
  },
  {
    id: 'availability-requests',
    name: 'Availability Requests',
    category: 'Core Scheduling',
    description: 'Collect candidate availability before scheduling',
    whatItDoes: 'Gathers candidate time preferences upfront',
    whyItMatters: 'Ensures mutual availability and reduces rescheduling needs',
    impacts: {
      schedulingVelocity: 60,
      candidateExperience: 75,
      schedulingIntegrity: 85
    },
    implementationComplexity: 'low',
    timeToValue: 'Immediate',
    icon: '⏰'
  },
  {
    id: 'interview-team-management',
    name: 'Interview Team Management',
    category: 'Core Scheduling',
    description: 'Pre-configured interview panels from Workday integration',
    whatItDoes: 'Auto-populates interviewers based on role requirements',
    whyItMatters: 'Ensures consistent interview panels and speeds up selection',
    impacts: {
      schedulingVelocity: 70,
      schedulingEfficiency: 80,
      schedulingUtilization: 60
    },
    implementationComplexity: 'medium',
    timeToValue: '2 weeks',
    icon: '👥'
  },

  // Automation & Intelligence
  {
    id: 'automated-reminders',
    name: 'Automated Reminders',
    category: 'Automation',
    description: 'Configurable reminder system for interviews',
    whatItDoes: 'Automatically sends reminders to candidates and interviewers',
    whyItMatters: 'Reduces no-shows by 30% and keeps participants informed',
    impacts: {
      schedulingIntegrity: 90,
      candidateExperience: 60,
      schedulingEfficiency: 65
    },
    implementationComplexity: 'low',
    timeToValue: 'Immediate',
    icon: '🔔'
  },
  {
    id: 'automation-rules',
    name: 'Automation Rules',
    category: 'Automation',
    description: 'Conditional workflows for interview events',
    whatItDoes: 'Triggers automatic actions based on specific conditions',
    whyItMatters: 'Reduces manual work by 70% and ensures consistent processes',
    impacts: {
      schedulingVelocity: 85,
      schedulingEfficiency: 90,
      schedulingIntegrity: 65
    },
    implementationComplexity: 'medium',
    timeToValue: '2 weeks',
    icon: '⚡'
  },
  {
    id: 'suggested-times',
    name: 'Suggested Times',
    category: 'Automation',
    description: 'Optimal time slot recommendations based on availability',
    whatItDoes: 'Analyzes calendars to suggest best mutual availability',
    whyItMatters: 'Reduces scheduling time by 40% and finds optimal slots',
    impacts: {
      schedulingVelocity: 90,
      schedulingEfficiency: 80,
      schedulingUtilization: 75
    },
    implementationComplexity: 'low',
    timeToValue: 'Immediate',
    icon: '🤖'
  },
  {
    id: 'smart-conflict-detection',
    name: 'Smart Conflict Detection',
    category: 'Automation',
    description: 'Automatic identification of scheduling conflicts',
    whatItDoes: 'Real-time calendar analysis to flag and prevent conflicts',
    whyItMatters: 'Prevents double-booking and ensures accuracy',
    impacts: {
      schedulingIntegrity: 95,
      schedulingEfficiency: 60,
      schedulingUtilization: 65
    },
    implementationComplexity: 'low',
    timeToValue: 'Immediate',
    icon: '🛡️'
  },

  // Workday Integration
  {
    id: 'workday-browser-extension',
    name: 'Workday Browser Extension',
    category: 'Integration',
    description: 'Native browser extension embedding Rooster in Workday',
    whatItDoes: 'Adds Rooster functionality directly within Workday pages',
    whyItMatters: 'Eliminates tab switching and improves efficiency by 15-20%',
    impacts: {
      schedulingVelocity: 65,
      schedulingEfficiency: 85,
      schedulingUtilization: 40
    },
    implementationComplexity: 'low',
    timeToValue: '1 week',
    icon: '🧩'
  },
  {
    id: 'workday-bidirectional-sync',
    name: 'Workday Bi-directional Sync',
    category: 'Integration',
    description: 'Real-time data synchronization with Workday HCM',
    whatItDoes: 'Maintains single source of truth between systems',
    whyItMatters: 'Eliminates double entry and maintains data integrity',
    impacts: {
      schedulingEfficiency: 75,
      schedulingIntegrity: 90
    },
    implementationComplexity: 'high',
    timeToValue: '3-4 weeks',
    icon: '🔄'
  },

  // Interviewer Management
  {
    id: 'interviewer-preferences',
    name: 'Interviewer Preferences',
    category: 'Resource Management',
    description: 'Customizable working hours and availability settings',
    whatItDoes: 'Respects interviewer schedules and preferences automatically',
    whyItMatters: 'Prevents burnout and improves interview quality',
    impacts: {
      schedulingIntegrity: 80,
      schedulingUtilization: 95,
      candidateExperience: 40
    },
    implementationComplexity: 'low',
    timeToValue: '1 week',
    icon: '⚙️'
  },
  {
    id: 'interviewer-tagging',
    name: 'Interviewer Tagging',
    category: 'Resource Management',
    description: 'Categorize interviewers by skills and attributes',
    whatItDoes: 'Enables quick selection of right interviewers for roles',
    whyItMatters: 'Ensures right fit and improves matching efficiency',
    impacts: {
      schedulingVelocity: 60,
      schedulingEfficiency: 65,
      schedulingUtilization: 70
    },
    implementationComplexity: 'low',
    timeToValue: '1 week',
    icon: '🏷️'
  },
  {
    id: 'roosterblocks',
    name: 'RoosterBlocks (Placeholders)',
    category: 'Resource Management',
    description: 'Calendar placeholders marking time as available for interviews',
    whatItDoes: 'Proactively dedicates time slots for interviews',
    whyItMatters: 'Optimizes interviewer availability and enables proactive scheduling',
    impacts: {
      schedulingVelocity: 75,
      schedulingIntegrity: 65,
      schedulingUtilization: 90
    },
    implementationComplexity: 'low',
    timeToValue: 'Immediate',
    icon: '📌'
  },
  {
    id: 'utilization-caps',
    name: 'Utilization Caps',
    category: 'Resource Management',
    description: 'Maximum interview limits per time period',
    whatItDoes: 'Automatically enforces interview load limits',
    whyItMatters: 'Prevents interviewer overload and ensures fair distribution',
    impacts: {
      schedulingIntegrity: 60,
      schedulingUtilization: 95
    },
    implementationComplexity: 'low',
    timeToValue: 'Immediate',
    icon: '🎯'
  },
  {
    id: 'buffer-time-management',
    name: 'Buffer Time Management',
    category: 'Resource Management',
    description: 'Configurable prep and debrief time around interviews',
    whatItDoes: 'Automatically blocks time before/after interviews',
    whyItMatters: 'Ensures quality interviews and prevents fatigue',
    impacts: {
      schedulingIntegrity: 70,
      schedulingUtilization: 85,
      candidateExperience: 35
    },
    implementationComplexity: 'low',
    timeToValue: 'Immediate',
    icon: '⏱️'
  },

  // Communication & Templates
  {
    id: 'email-templates',
    name: 'Email Templates',
    category: 'Communication',
    description: 'Customizable communication templates with placeholders',
    whatItDoes: 'Standardizes and automates interview communications',
    whyItMatters: 'Ensures consistent messaging and saves time',
    impacts: {
      schedulingEfficiency: 70,
      candidateExperience: 65,
      schedulingIntegrity: 30
    },
    implementationComplexity: 'low',
    timeToValue: '1 week',
    icon: '📧'
  },
  {
    id: 'sms-whatsapp',
    name: 'SMS/WhatsApp Integration',
    category: 'Communication',
    description: 'Text messaging for confirmations and reminders',
    whatItDoes: 'Sends instant communications via text messaging',
    whyItMatters: 'Improves response rates by 40% and reaches candidates effectively',
    impacts: {
      schedulingVelocity: 80,
      candidateExperience: 75,
      schedulingIntegrity: 85
    },
    implementationComplexity: 'medium',
    timeToValue: '2 weeks',
    icon: '💬'
  },

  // Calendar & Video Integration
  {
    id: 'calendar-integration',
    name: 'Office 365/Google Calendar Integration',
    category: 'Integration',
    description: 'Real-time sync with calendar systems',
    whatItDoes: 'Shows accurate availability and prevents double-booking',
    whyItMatters: 'Maintains calendar integrity and enables seamless workflow',
    impacts: {
      schedulingVelocity: 65,
      schedulingIntegrity: 90,
      schedulingUtilization: 85
    },
    implementationComplexity: 'medium',
    timeToValue: '1-2 weeks',
    icon: '📅'
  },
  {
    id: 'video-integration',
    name: 'Zoom/Teams/WebEx Integration',
    category: 'Integration',
    description: 'Automatic video meeting creation',
    whatItDoes: 'Automatically creates and includes video links in invites',
    whyItMatters: 'Eliminates manual link creation and ensures meeting setup',
    impacts: {
      schedulingEfficiency: 75,
      candidateExperience: 60,
      schedulingIntegrity: 65
    },
    implementationComplexity: 'low',
    timeToValue: '1 week',
    icon: '📹'
  },

  // Specialized Modules
  {
    id: 'roosterlinks',
    name: 'Rooster Prospecting',
    category: 'Specialized',
    description: 'Sourcer tools for passive candidate engagement',
    whatItDoes: 'Creates shareable links for instant meeting booking',
    whyItMatters: 'Enables proactive recruiting and streamlines initial conversations',
    impacts: {
      schedulingVelocity: 85,
      schedulingEfficiency: 80,
      candidateExperience: 80
    },
    implementationComplexity: 'low',
    timeToValue: '1 week',
    icon: '🔗'
  },

  // Mobile & Accessibility
  {
    id: 'mobile-responsive',
    name: 'Mobile Responsive UI',
    category: 'Accessibility',
    description: 'Optimized interface for all devices',
    whatItDoes: 'Enables full functionality on mobile devices',
    whyItMatters: 'Supports on-the-go scheduling and improves accessibility',
    impacts: {
      schedulingVelocity: 60,
      candidateExperience: 75,
      schedulingEfficiency: 50
    },
    implementationComplexity: 'low',
    timeToValue: 'Immediate',
    icon: '📱'
  }
];

// Helper function to get top features by component
export function getTopFeaturesForComponent(
  component: keyof RoosterFeature['impacts'],
  limit: number = 5
): RoosterFeature[] {
  return ROOSTER_FEATURES
    .filter(feature => feature.impacts[component] !== undefined)
    .sort((a, b) => (b.impacts[component] || 0) - (a.impacts[component] || 0))
    .slice(0, limit);
}

// Helper function to get features by category
export function getFeaturesByCategory(category: string): RoosterFeature[] {
  return ROOSTER_FEATURES.filter(feature => feature.category === category);
}

// Helper function to calculate total impact for a feature
export function calculateFeatureTotalImpact(feature: RoosterFeature): number {
  const weights = {
    schedulingVelocity: 0.30,
    schedulingEfficiency: 0.25,
    candidateExperience: 0.20,
    schedulingIntegrity: 0.15,
    schedulingUtilization: 0.10
  };

  let totalImpact = 0;
  Object.entries(feature.impacts).forEach(([component, impact]) => {
    if (impact) {
      totalImpact += impact * weights[component as keyof typeof weights];
    }
  });

  return Math.round(totalImpact);
}

// Helper function to get recommended features based on weak components
export function getRecommendedFeatures(
  componentScores: {
    schedulingVelocity: number;
    schedulingEfficiency: number;
    candidateExperience: number;
    schedulingIntegrity: number;
    schedulingUtilization: number;
  },
  limit: number = 5
): RoosterFeature[] {
  // Find the weakest components (lowest scores)
  const components = Object.entries(componentScores)
    .sort(([, a], [, b]) => a - b)
    .slice(0, 2) // Focus on top 2 weakest components
    .map(([component]) => component as keyof typeof componentScores);

  // Get features that impact the weak components most
  const recommendedFeatures = new Map<string, { feature: RoosterFeature; score: number }>();

  components.forEach(component => {
    const topFeatures = getTopFeaturesForComponent(component, 10);
    topFeatures.forEach(feature => {
      const existingEntry = recommendedFeatures.get(feature.id);
      const impactScore = feature.impacts[component] || 0;
      
      if (!existingEntry || existingEntry.score < impactScore) {
        recommendedFeatures.set(feature.id, {
          feature,
          score: impactScore
        });
      }
    });
  });

  // Sort by score and return top features
  return Array.from(recommendedFeatures.values())
    .sort((a, b) => b.score - a.score)
    .map(entry => entry.feature)
    .slice(0, limit);
}

// Export categories for filtering
export const FEATURE_CATEGORIES = [
  'Core Scheduling',
  'Automation',
  'Integration',
  'Resource Management',
  'Communication',
  'Specialized',
  'Accessibility'
];

// Export impact level definitions
export const IMPACT_LEVELS = {
  HIGH: { min: 70, max: 100, label: 'High', color: '#4f46e5' },    // Indigo
  MEDIUM: { min: 40, max: 69, label: 'Medium', color: '#3b82f6' }, // Blue
  LOW: { min: 10, max: 39, label: 'Low', color: '#9ca3af' }        // Gray
};

export function getImpactLevel(value: number) {
  if (value >= IMPACT_LEVELS.HIGH.min) return IMPACT_LEVELS.HIGH;
  if (value >= IMPACT_LEVELS.MEDIUM.min) return IMPACT_LEVELS.MEDIUM;
  return IMPACT_LEVELS.LOW;
}